import React, { useState } from 'react';
import {
  ListItem,
  ListItemText,
  List,
  Checkbox,
  AccordionDetails,
  Grid,
  Button,
  makeStyles,
  ListItemIcon,
  TextField,
  Chip
} from '@material-ui/core';

type ShareItem = {
  id: string;
  text: string;
  preselected: boolean;
  isNew?: boolean;
  isPending?: boolean;
};

type ShareAction = {
  text: string;
  callback: (ids: string[]) => void;
};

type ShareSectionListProps = {
  items: ShareItem[];
  actions?: ShareAction[];
  emptyLabel?: string;
};

const useClasses = makeStyles(theme => ({
  checkbox: {
    margin: 0,
    padding: 0
  },
  actionButton: {
    marginTop: theme.spacing(1),
    marginRight: 5
  },
  item: {
    margin: 0,
    padding: 0
  },
  listContainer: {
    maxHeight: 200,
    overflowY: 'auto',
    width: '100%'
  },
  searchField: {
    marginBottom: theme.spacing(1),
    width: '100%'
  },
  newChip: {
    marginLeft: theme.spacing(1),
    backgroundColor: '#4caf50',
    color: '#fff',
    height: 18,
    fontSize: '0.7rem'
  }
}));

const ShareSectionList = function ShareSectionList({
  items,
  actions,
  emptyLabel = 'No data available.'
}: ShareSectionListProps): React.ReactElement {
  const classes = useClasses();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');

  const onChecked = function onChecked(id: string): void {
    const newSelectedIds = selectedIds.includes(id)
      ? selectedIds.filter(i => i !== id)
      : [...selectedIds, id];
    setSelectedIds(newSelectedIds);
  };

  const filteredItems = items.filter(i =>
    i.text.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <AccordionDetails>
      <Grid container direction='column'>
        {items.length > 5 && (
          <Grid item>
            <TextField
              className={classes.searchField}
              size='small'
              variant='outlined'
              placeholder='Search...'
              inputProps={{ 'aria-label': 'Search items' }}
              value={searchText}
              onChange={(e): void => setSearchText(e.target.value)}
            />
          </Grid>
        )}

        <Grid item className={`${classes.item} ${classes.listContainer}`}>
          <List dense>
            {filteredItems.length === 0 && (
              <ListItem>
                <ListItemText
                  primary={
                    items.length === 0
                      ? emptyLabel
                      : 'No results match your search.'
                  }
                  primaryTypographyProps={{
                    color: 'textSecondary',
                    align: 'center'
                  }}
                />
              </ListItem>
            )}
            {filteredItems.map(i => (
              <ListItem key={i.id} className={classes.item}>
                <ListItemIcon>
                  <Checkbox
                    disabled={i.preselected}
                    onChange={(): void => onChecked(i.id)}
                    checked={i.preselected || selectedIds.includes(i.id)}
                    className={classes.checkbox}
                  />
                </ListItemIcon>
                <ListItemText>
                  {i.text}
                  {i.isNew && (
                    <Chip
                      label='New!'
                      size='small'
                      className={classes.newChip}
                    />
                  )}
                </ListItemText>
              </ListItem>
            ))}
          </List>
        </Grid>

        {items.length > 0 && (
          <Grid container justifyContent='flex-end'>
            {actions?.map(a => (
              <Button
                size='small'
                disabled={selectedIds.length === 0}
                key={a.text}
                color='inherit'
                variant='outlined'
                onClick={(): void => {
                  a.callback(selectedIds);
                }}
                className={classes.actionButton}
              >
                {a.text}
              </Button>
            ))}
          </Grid>
        )}
      </Grid>
    </AccordionDetails>
  );
};

export default ShareSectionList;
